import React, { useEffect, useState } from 'react';
import { ActionIcon, Loader, Modal, Tabs } from '@mantine/core';
import {
  APPROVAL_STATUS_LABELS,
  getApprovalActionLabel,
  METHOD_LABELS,
  ORDER_STATUS_LABELS,
  STATUS_LABELS,
} from '../monitoring-page.constants';
import { resolveTip } from 'shared-models/browser';
import type {
  AdminApprovalRequestDetailView,
  AdminMergeAuditView,
  AdminPaymentlineView,
  AdminTransferAuditView,
} from 'shared-models/browser';
import { formatDateTime, formatMoney } from '../monitoring-page.utils';
import { groupOrderlineSelections } from '../../../utils/orderline-selections';
import { adminApprovalRequestsService } from '../../../services/admin-approval-requests/admin-approval-requests.service';
import { adminTransferAuditService } from '../../../services/admin-transfer-audit/admin-transfer-audit.service';
import { adminMergeAuditService } from '../../../services/admin-merge-audit/admin-merge-audit.service';
import { ApprovalRequestDetail } from './ApprovalRequestDetail';
import { TransferAuditDetail } from './TransferAuditDetail/TransferAuditDetail';
import { MergeAuditDetail } from './MergeAuditDetail/MergeAuditDetail';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

// Above the base order-detail modal (Mantine default 200) so drill-in modals always stack in front.
const NESTED_MODAL_Z_INDEX = 1200;

type OrderMonitoringDetailModalProps = Pick<
  UseMonitoringPage,
  | 'selectedOrderDetail'
  | 'selectedOrderIndex'
  | 'ordersRows'
  | 'isOrderDetailLoading'
  | 'closeOrderDetail'
  | 'prevOrderDetail'
  | 'nextOrderDetail'
  | 'openAuditModal'
>;

export const OrderMonitoringDetailModal: React.FC<OrderMonitoringDetailModalProps> = ({
  selectedOrderDetail,
  selectedOrderIndex,
  ordersRows,
  isOrderDetailLoading,
  closeOrderDetail,
  prevOrderDetail,
  nextOrderDetail,
  openAuditModal,
}) => {
  const opened = selectedOrderIndex !== null;
  const order = selectedOrderDetail?.order ?? null;

  type PrintLog = NonNullable<typeof selectedOrderDetail>['printLogs'][number];

  const [approvalDetail, setApprovalDetail] = useState<AdminApprovalRequestDetailView | null>(null);
  const [transferDetail, setTransferDetail] = useState<AdminTransferAuditView | null>(null);
  const [mergeDetail, setMergeDetail] = useState<AdminMergeAuditView | null>(null);
  const [printDetail, setPrintDetail] = useState<PrintLog | null>(null);
  const [nestedOpen, setNestedOpen] = useState<'approval' | 'transfer' | 'merge' | 'print' | null>(null);
  const [isNestedLoading, setIsNestedLoading] = useState(false);
  const [nestedError, setNestedError] = useState<string | null>(null);

  const closeNested = () => {
    setNestedOpen(null);
    setNestedError(null);
    setApprovalDetail(null);
    setTransferDetail(null);
    setMergeDetail(null);
    setPrintDetail(null);
  };

  // Clear drill-in state when the order modal closes so a reopen never shows a stale nested detail.
  useEffect(() => {
    if (selectedOrderIndex === null) {
      setNestedOpen(null);
      setNestedError(null);
      setApprovalDetail(null);
      setTransferDetail(null);
      setMergeDetail(null);
      setPrintDetail(null);
    }
  }, [selectedOrderIndex]);

  const openApprovalDetail = async (requestId: number) => {
    setNestedOpen('approval');
    setIsNestedLoading(true);
    setNestedError(null);
    setApprovalDetail(null);
    try {
      setApprovalDetail(await adminApprovalRequestsService.getDetail(requestId));
    } catch (fetchError: unknown) {
      setNestedError(fetchError instanceof Error ? fetchError.message : 'טעינת הפרטים נכשלה');
    } finally {
      setIsNestedLoading(false);
    }
  };

  const openTransferDetail = async (transferId: number) => {
    setNestedOpen('transfer');
    setIsNestedLoading(true);
    setNestedError(null);
    setTransferDetail(null);
    try {
      setTransferDetail(await adminTransferAuditService.getDetail(transferId));
    } catch (fetchError: unknown) {
      setNestedError(fetchError instanceof Error ? fetchError.message : 'טעינת הפרטים נכשלה');
    } finally {
      setIsNestedLoading(false);
    }
  };

  const openMergeDetail = async (mergeId: number) => {
    setNestedOpen('merge');
    setIsNestedLoading(true);
    setNestedError(null);
    setMergeDetail(null);
    try {
      setMergeDetail(await adminMergeAuditService.getDetail(mergeId));
    } catch (fetchError: unknown) {
      setNestedError(fetchError instanceof Error ? fetchError.message : 'טעינת הפרטים נכשלה');
    } finally {
      setIsNestedLoading(false);
    }
  };

  const openPrintDetail = (log: PrintLog) => {
    setNestedError(null);
    setPrintDetail(log);
    setNestedOpen('print');
  };

  const openPaymentlineAudit = (line: NonNullable<typeof selectedOrderDetail>['paymentlines'][number]) => {
    if (!order) {
      return;
    }
    void openAuditModal({ ...line, orderCode: order.orderCode } as unknown as AdminPaymentlineView);
  };

  const transferKindLabel = (kind: string) => {
    if (kind === 'partial_transfer') {
      return 'העברה חלקית';
    }
    if (kind === 'full_transfer') {
      return 'העברה מלאה';
    }
    if (kind === 'merge_transfer') {
      return 'העברה במיזוג';
    }
    return kind;
  };

  const mergeKindLabel = (kind: string) => {
    if (kind === 'merge') {
      return 'מיזוג';
    }
    if (kind === 'partial_split') {
      return 'פיצול חלקי';
    }
    if (kind === 'full_split') {
      return 'פיצול מלא';
    }
    return kind;
  };

  const paymentStatusClassName = (status: string): string => {
    if (status === 'done') {
      return classes.paymentStatusDone;
    }
    if (status === 'canceled_split' || status === 'canceled_reversed' || status === 'canceled_reset') {
      return classes.paymentStatusCanceled;
    }
    return classes.paymentStatusActive;
  };

  const round1 = (value: number): number => Math.round(value * 10) / 10;

  const billableTotal = (() => {
    if (!selectedOrderDetail || !order) {
      return 0;
    }
    const isTakeAway = order.orderType === 'take_away';
    const linesBase = selectedOrderDetail.orderlines
      .filter((line) =>
        line.quantity > 0
        && (
          line.status === 'sent'
          || line.status === 'skipped'
          || (line.status === 'pending' && isTakeAway)
        ),
      )
      .reduce((sum, line) => sum + line.unitPrice * line.quantity - (line.discountInput ?? 0), 0);

    let orderDiscount = 0;
    if (order.discountMode === 'amount') {
      orderDiscount = Math.min(order.discountInput ?? 0, linesBase);
    } else if (order.discountMode === 'percentage') {
      orderDiscount = Math.min(round1((linesBase * (order.discountInput ?? 0)) / 100), linesBase);
    }
    return Math.max(0, round1(linesBase - orderDiscount));
  })();

  const globalTipTotal = billableTotal > 0
    ? resolveTip(order?.tipInputMode ?? null, order?.tipInputValue ?? null, billableTotal)
    : 0;

  const paymentLineTotal = (line: NonNullable<typeof selectedOrderDetail>['paymentlines'][number]): number => {
    const lineTip = resolveTip(line.tipInputMode ?? null, line.tipInputValue ?? null, line.amount);
    const explicitGlobalShare = line.globalTipCollected ?? 0;
    const inferredGlobalShare =
      explicitGlobalShare > 0
        ? explicitGlobalShare
        : line.status === 'canceled_split' && billableTotal > 0
          ? round1((globalTipTotal * line.amount) / billableTotal)
          : 0;
    return line.amount + lineTip + inferredGlobalShare;
  };

  return (
    <Modal
      opened={opened}
      onClose={closeOrderDetail}
      title={order ? `#${order.orderCode} ${order.companyName}` : 'פרטי הזמנה'}
      centered
      size="xl"
      closeButtonProps={{ 'data-testid': 'monitoring-order-detail-close' } as Record<string, string>}
    >
      {isOrderDetailLoading || !selectedOrderDetail || !order ? (
        <div className={classes.loadingWrap}><Loader /></div>
      ) : (
        <div className={classes.detailRoot}>
          <div className={classes.modalActions}>
            <ActionIcon className={classes.arrowBtn} onClick={prevOrderDetail} disabled={(selectedOrderIndex ?? 0) <= 0} variant="default" size="sm" aria-label="רשומה קודמת" data-testid="monitoring-order-detail-prev">{'‹'}</ActionIcon>
            <span className={classes.subtitle}>{`${(selectedOrderIndex ?? 0) + 1} מתוך ${ordersRows.length}`}</span>
            <ActionIcon className={classes.arrowBtn} onClick={nextOrderDetail} disabled={(selectedOrderIndex ?? 0) >= ordersRows.length - 1} variant="default" size="sm" aria-label="רשומה הבאה" data-testid="monitoring-order-detail-next">{'›'}</ActionIcon>
          </div>

          <Tabs defaultValue="summary">
            <Tabs.List className={classes.tabsList}>
              <Tabs.Tab value="summary" data-testid="monitoring-order-tab-summary">{'סיכום'}</Tabs.Tab>
              <Tabs.Tab value="payments" data-testid="monitoring-order-tab-payments">{`תשלומים (${selectedOrderDetail.paymentlines.length})`}</Tabs.Tab>
              <Tabs.Tab value="items" data-testid="monitoring-order-tab-items">{`מוצרים (${selectedOrderDetail.orderlines.length})`}</Tabs.Tab>
              <Tabs.Tab value="approvals" data-testid="monitoring-order-tab-approvals">{`בקשות אישור (${selectedOrderDetail.approvalRequests.length})`}</Tabs.Tab>
              <Tabs.Tab value="prints" data-testid="monitoring-order-tab-prints">{`הדפסות ומטבח (${selectedOrderDetail.printLogs.length})`}</Tabs.Tab>
              <Tabs.Tab value="movement" data-testid="monitoring-order-tab-movement">{`העברות/מיזוגים (${selectedOrderDetail.transferHistory.length + selectedOrderDetail.mergeHistory.length})`}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="summary" className={classes.detailPanel}>
              <div className={classes.detailsGrid}>
                <section className={classes.detailsSection}>
                  <h4 className={classes.sectionTitle}>{'פרטי הזמנה'}</h4>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'מספר הזמנה:'}</span><span className={classes.detailValue}>{order.orderCode}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'משמרת:'}</span><span className={classes.detailValue}>{order.sessionName}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'שולחנות:'}</span><span className={classes.detailValue}>{order.tableLabels.join(', ') || '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'סטטוס:'}</span><span className={classes.detailValue}>{ORDER_STATUS_LABELS[order.status]}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'סוג:'}</span><span className={classes.detailValue}>{order.orderType === 'dine_in' ? 'ישיבה' : 'טייק אווי'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'שם לקוח:'}</span><span className={classes.detailValue}>{order.guestName || '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'סועדים:'}</span><span className={classes.detailValue}>{String(order.dinersCount)}</span></div>
                </section>

                <section className={classes.detailsSection}>
                  <h4 className={classes.sectionTitle}>{'כספים וזמנים'}</h4>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'סה"כ הזמנה:'}</span><span className={classes.detailValue}>{`${formatMoney(order.totalAmount)} ₪`}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'סה"כ ששולם:'}</span><span className={classes.detailValue}>{`${formatMoney(order.paidAmount)} ₪`}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'הנחה כוללת:'}</span><span className={classes.detailValue}>{order.discountMode ? `${order.discountMode} (${order.discountInput ?? 0})` : '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'טיפ:'}</span><span className={classes.detailValue}>{order.tipInputMode ? `${order.tipInputMode} (${order.tipInputValue ?? 0})` : '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'מי פתח:'}</span><span className={classes.detailValue}>{order.openedByName ?? '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'מתי נפתחה:'}</span><span className={classes.detailValue}>{formatDateTime(order.openedAt)}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'מי סגר:'}</span><span className={classes.detailValue}>{order.closedByName ?? '-'}</span></div>
                  <div className={classes.detailField}><span className={classes.detailLabel}>{'מתי נסגרה:'}</span><span className={classes.detailValue}>{formatDateTime(order.closedAt)}</span></div>
                </section>
              </div>
            </Tabs.Panel>

            <Tabs.Panel value="payments" className={classes.detailPanel}>
              <section className={classes.detailsSection}>
                <h4 className={classes.sectionTitle}>{'תשלומים בהזמנה'}</h4>
                <div className={classes.auditList}>
                  {selectedOrderDetail.paymentlines.map((line) => (
                    <div key={line.id} className={`${classes.auditRow} ${classes.clickableRow}`} onClick={() => openPaymentlineAudit(line)} data-testid={`monitoring-order-payment-${line.id}`}>
                      <div className={classes.auditHeader}>
                        <span>{`#${line.id} ${METHOD_LABELS[line.method]}`}</span>
                        <span>{`${formatMoney(paymentLineTotal(line))} ₪`}</span>
                      </div>
                      <div className={classes.paymentStatusRow}>
                        <span className={classes.paymentStatusLabel}>{'סטטוס:'}</span>
                        <span className={`${classes.paymentStatusValue} ${paymentStatusClassName(line.status)}`}>{STATUS_LABELS[line.status]}</span>
                      </div>
                      <div className={classes.auditMeta}>{`שולם על ידי: ${line.paidByName ?? '-'}`}</div>
                      <div className={classes.auditMeta}>{`עודכן לאחרונה: ${line.changedByName ?? '-'}`}</div>
                    </div>
                  ))}
                  {selectedOrderDetail.paymentlines.length === 0 && <div className={classes.empty}>{'אין תשלומים בהזמנה זו.'}</div>}
                </div>
              </section>
            </Tabs.Panel>

            <Tabs.Panel value="items" className={classes.detailPanel}>
              <section className={classes.detailsSection}>
                <h4 className={classes.sectionTitle}>{'מוצרים בהזמנה (כולל מבוטלים)'}</h4>
                <table className={classes.itemsTable}>
                  <thead>
                    <tr>
                      <th>{'מוצר'}</th>
                      <th>{'כמות'}</th>
                      <th>{'סטטוס'}</th>
                      <th>{'מחיר יחידה'}</th>
                      <th>{'הנחה'}</th>
                      <th>{'הערה'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrderDetail.orderlines.map((line) => {
                      const selectionGroups = groupOrderlineSelections(line.selections);
                      return (
                        <tr key={line.id}>
                          <td>
                            {line.productName ?? 'פריט כללי'}
                            {selectionGroups.length > 0 && (
                              <ul className={classes.itemSelections}>
                                {selectionGroups.map((group, index) => (
                                  <li key={`${group.groupName}-${index}`} className={classes.itemSelectionRow}>
                                    {group.groupName && <span className={classes.itemSelectionGroupLabel}>{`${group.groupName}: `}</span>}
                                    {group.options.map((opt) => opt.name).join(', ')}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td>{line.quantity}</td>
                          <td>{line.status}</td>
                          <td>{formatMoney(line.unitPrice)}</td>
                          <td>{line.isDiscounted ? `כן (${formatMoney(line.discountInput ?? 0)})` : 'לא'}</td>
                          <td>{line.note ? <span className={classes.itemNote}>{line.note}</span> : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            </Tabs.Panel>

            <Tabs.Panel value="approvals" className={classes.detailPanel}>
              <section className={classes.detailsSection}>
                <h4 className={classes.sectionTitle}>{'בקשות אישור מקושרות להזמנה'}</h4>
                <div className={classes.auditList}>
                  {selectedOrderDetail.approvalRequests.map((request) => (
                    <div key={request.id} className={`${classes.auditRow} ${classes.clickableRow}`} onClick={() => openApprovalDetail(request.id)} data-testid={`monitoring-order-approval-${request.id}`}>
                      <div className={classes.auditHeader}>
                        <span>{getApprovalActionLabel(request)}</span>
                        <span>{APPROVAL_STATUS_LABELS[request.status]}</span>
                      </div>
                      <div className={classes.auditMeta}>{`מבקש: ${request.requestedByName}`}</div>
                      <div className={classes.auditMeta}>{`מאשר/דוחה: ${request.decidedByName ?? '-'}`}</div>
                      <div className={classes.auditMeta}>{formatDateTime(request.createdAt)}</div>
                    </div>
                  ))}
                  {selectedOrderDetail.approvalRequests.length === 0 && <div className={classes.empty}>{'אין בקשות אישור.'}</div>}
                </div>
              </section>
            </Tabs.Panel>

            <Tabs.Panel value="prints" className={classes.detailPanel}>
              <section className={classes.detailsSection}>
                <h4 className={classes.sectionTitle}>{'לוג הדפסות ושליחה למטבח'}</h4>
                <div className={classes.auditList}>
                  {selectedOrderDetail.printLogs.map((log) => (
                    <div key={log.id} className={`${classes.auditRow} ${classes.clickableRow}`} onClick={() => openPrintDetail(log)} data-testid={`monitoring-order-print-${log.id}`}>
                      <div className={classes.printLogHeadRow}>
                        {log.printImageUrl && (
                          <div className={classes.kitchenThumb}>
                            <img src={log.printImageUrl} alt={'הדפסה'} className={classes.kitchenThumbImage} />
                          </div>
                        )}
                        <div className={classes.auditHeader}>
                          <span>{printKindLabel(log.kind)}</span>
                          <span>{log.status}</span>
                        </div>
                      </div>
                      <div className={classes.auditMeta}>{`עובד: ${log.workerName ?? '-'}`}</div>
                      <div className={classes.auditMeta}>{`מדפסת: ${log.printerName ?? '-'}`}</div>
                      <div className={classes.auditMeta}>{formatDateTime(log.occurredAt ?? log.createdAt)}</div>
                    </div>
                  ))}
                  {selectedOrderDetail.printLogs.length === 0 && <div className={classes.empty}>{'אין לוג הדפסות.'}</div>}
                </div>
              </section>
            </Tabs.Panel>

            <Tabs.Panel value="movement" className={classes.detailPanel}>
              <div className={classes.detailsGrid}>
                <section className={classes.detailsSection}>
                  <h4 className={classes.sectionTitle}>{'היסטוריית העברות'}</h4>
                  <div className={classes.auditList}>
                    {selectedOrderDetail.transferHistory.map((entry) => (
                      <div key={entry.id} className={`${classes.auditRow} ${classes.clickableRow}`} onClick={() => openTransferDetail(entry.id)} data-testid={`monitoring-order-transfer-${entry.id}`}>
                        <div className={classes.auditHeader}><span>{transferKindLabel(entry.transferKind)}</span><span>{formatDateTime(entry.createdAt)}</span></div>
                        <div className={classes.auditMeta}>{`${entry.sourceOrderCode ?? '-'} ← ${entry.targetOrderCode ?? '-'}`}</div>
                        <div className={classes.auditMeta}>{`בוצע ע"י: ${entry.performedByName ?? '-'}`}</div>
                      </div>
                    ))}
                    {selectedOrderDetail.transferHistory.length === 0 && <div className={classes.empty}>{'אין היסטוריית העברות.'}</div>}
                  </div>
                </section>

                <section className={classes.detailsSection}>
                  <h4 className={classes.sectionTitle}>{'היסטוריית מיזוגים/פיצולים'}</h4>
                  <div className={classes.auditList}>
                    {selectedOrderDetail.mergeHistory.map((entry) => (
                      <div key={entry.id} className={`${classes.auditRow} ${classes.clickableRow}`} onClick={() => openMergeDetail(entry.id)} data-testid={`monitoring-order-merge-${entry.id}`}>
                        <div className={classes.auditHeader}><span>{mergeKindLabel(entry.kind)}</span><span>{formatDateTime(entry.createdAt)}</span></div>
                        <div className={classes.auditMeta}>{`שולחן ראשי: ${entry.mainTableName ?? '-'}`}</div>
                        <div className={classes.auditMeta}>{`שולחנות לפני: ${entry.mergedTables.map((table) => table.tableName).join(', ') || '-'}`}</div>
                        <div className={classes.auditMeta}>{`שולחנות אחרי: ${entry.mergedTablesAfter.map((table) => table.tableName).join(', ') || '-'}`}</div>
                      </div>
                    ))}
                    {selectedOrderDetail.mergeHistory.length === 0 && <div className={classes.empty}>{'אין היסטוריית מיזוגים/פיצולים.'}</div>}
                  </div>
                </section>
              </div>
            </Tabs.Panel>
          </Tabs>
        </div>
      )}

      <Modal
        opened={nestedOpen !== null}
        onClose={closeNested}
        title={
          nestedOpen === 'approval' ? 'פרטי בקשת אישור'
            : nestedOpen === 'transfer' ? 'פרטי העברה'
              : nestedOpen === 'merge' ? 'פרטי מיזוג/פיצול'
                : printDetail?.printImageUrl ? 'תצוגת הדפסת מטבח'
                  : 'פריטים שנשלחו'
        }
        centered
        size="xl"
        zIndex={NESTED_MODAL_Z_INDEX}
        closeButtonProps={{ 'data-testid': 'monitoring-order-nested-close' } as Record<string, string>}
      >
        {isNestedLoading ? (
          <div className={classes.loadingWrap}><Loader /></div>
        ) : nestedError ? (
          <p className={classes.error}>{nestedError}</p>
        ) : (
          <>
            {nestedOpen === 'approval' && approvalDetail && <ApprovalRequestDetail detail={approvalDetail} />}
            {nestedOpen === 'transfer' && transferDetail && <TransferAuditDetail row={transferDetail} />}
            {nestedOpen === 'merge' && mergeDetail && <MergeAuditDetail row={mergeDetail} />}
            {nestedOpen === 'print' && printDetail && (
              printDetail.printImageUrl ? (
                <img src={printDetail.printImageUrl} alt={'הדפסת מטבח'} className={classes.kitchenPrintPreviewImage} />
              ) : printDetail.items.length > 0 ? (
                <ul className={classes.printItems}>
                  {printDetail.items.map((item, index) => (
                    <li key={`${item.name}-${index}`} className={classes.printItemRow}>
                      {`${item.name} ×${item.quantity}`}
                      {item.routeLabel && <span className={classes.printItemRoute}>{` · ${item.routeLabel}`}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={classes.empty}>{'אין פריטים להצגה.'}</div>
              )
            )}
          </>
        )}
      </Modal>
    </Modal>
  );
};

const printKindLabel = (kind: string) => {
  switch (kind) {
    case 'green_fire':
      return 'שליחה למטבח';
    case 'red_fire':
      return 'דילוג ';
    case 'receipt_print':
      return 'הדפסת חשבון';
    case 'reprint':
      return 'הדפסה חוזרת';
    default:
      return kind;
  }
};
